/**
 * PATH: packages/ui/src/lib/brandingPref.ts
 * FILE_ID: UI-LIB-BRANDINGPREF-001
 * PURPOSE: Synchronous device cache of the "Powered by Quotech" email
 *          footer default (opt-in) so the composer can show the footer
 *          chip at mount without awaiting a fetch.
 * WHY: FEAT-2026-07-07-GTM-FOOTER-OPT-IN — operator: the marketing banner
 *      must be visible in the composer when it will ship, removable
 *      inline, and OFF unless explicitly enabled in Settings.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────────────────┐   ┌──────────────┐
 *   │ Settings UI  │ → │ setQuotechBrandingDefault()   │ → │ localStorage │
 *   └──────────────┘   └──────────────────────────────┘   └──────────────┘
 *   Composer mount → getQuotechBrandingDefault() → footer chip on/off.
 * SOURCE OF TRUTH = the backend (`quotech_branding` in /api/settings/email/).
 *   This localStorage value is a SYNCHRONOUS CACHE/MIRROR — EmailTab writes
 *   it on prefs load/save; the composer self-heals via a background fetch
 *   (email-hub brandingDefaultSync). Cache-empty reads as FALSE, matching
 *   the backend's opt-in default, so a fresh device never shows the chip
 *   for a footer that won't ship.
 * RELATED:
 *   - packages/email-hub/.../compose/brandingDefaultSync.ts (server sync)
 *   - packages/email-hub/.../pages/ComposePage.tsx (the chip)
 *   - packages/ui/.../settings/EmailTab.tsx (the toggle)
 */

/** localStorage key for the device-local branding default. */
export const QUOTECH_BRANDING_STORAGE_KEY = 'quotech.emailBrandingFooterDefault'

/** Resolve a usable Storage, or null when unavailable (SSR / private mode). */
function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

/**
 * Read the cached branding default. FALSE when unset/unavailable — the
 * footer is opt-in, so absence of a choice means it does not ship.
 */
export function getQuotechBrandingDefault(storage: Storage | null = safeStorage()): boolean {
  if (!storage) return false
  try {
    return storage.getItem(QUOTECH_BRANDING_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

/** Persist the branding default. No-op when storage is unavailable. */
export function setQuotechBrandingDefault(
  enabled: boolean,
  storage: Storage | null = safeStorage(),
): void {
  if (!storage) return
  try {
    storage.setItem(QUOTECH_BRANDING_STORAGE_KEY, enabled ? '1' : '0')
  } catch {
    /* quota / private mode — preference simply doesn't persist */
  }
}
